/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Checkbox, ProgressBar, TextInput } from '@carbon/react';

import EntityTable from 'components/EntityTable/EntityTable';
import {
  EXTRACTED_RESULTS_NO_DATA_ID,
  EXTRACTED_RESULTS_FIELD_NAME,
  EXTRACTED_RESULTS_VARIABLE_NAME,
  EXTRACTED_RESULTS_VALUE,
  EXTRACTED_RESULTS_CONFIDENCE
} from 'App/Pages/Project/IdpApplication/idp-project/utils/constants';

import * as Styled from './FormsView.styled';
/** @typedef {import('../types').Field} Field */
/**
 * @param {{
 *   fields: Field[],
 *   onToggleActive: (id: string, active: boolean) => void,
 *   onToggleAllActive: (active: boolean) => void,
 *   onNameChange: (id: string, value: string) => void,
 *   nameValidation?: { [id: string]: string }
 * }} props
 * @returns {JSX.Element}
 */
export function FormsView({ fields, onToggleActive, onToggleAllActive, onNameChange, nameValidation = {} }) {
  const dataRows = fields.filter((row) => row.id !== EXTRACTED_RESULTS_NO_DATA_ID);
  const allRowsActive = dataRows.length > 0 && dataRows.every((row) => row.active);
  const someRowsActive = dataRows.length > 0 && dataRows.some((row) => row.active) && !allRowsActive;

  const containsSearchWord = (value, searchWord) => String(value).toLowerCase().includes(searchWord.toLowerCase());

  const getComparableValue = (value) => value.toLowerCase();

  const getPercentageComparableValue = (value) => {
    const numericValue = typeof value === 'number' ? value : parseFloat(String(value).replace('%', ''));
    return isNaN(numericValue) ? 0 : numericValue;
  };

  const tableHeaders = [
    {
      key: 'id',
      header: (
        <Checkbox
          id="select-all-checkbox"
          labelText="Activate all rows"
          checked={allRowsActive}
          indeterminate={someRowsActive}
          onChange={(_e, { checked }) => onToggleAllActive(checked)}
          hideLabel
          disabled={dataRows.length === 0}
        />
      ),
      renderer: (id) => {
        if (id === EXTRACTED_RESULTS_NO_DATA_ID) {
          return null;
        }
        const row = fields.find((f) => f.id === id);
        return (
          <Checkbox
            id={id}
            labelText={`Activate row ${id}`}
            checked={row?.active}
            onChange={(_event, { checked }) => onToggleActive(id, checked)}
            hideLabel
          />
        );
      }
    },
    {
      key: 'replacedName',
      header: EXTRACTED_RESULTS_VARIABLE_NAME,
      renderer: Object.assign(
        (replacedName) => {
          const row = fields.find(
            (f) => f.replacedName === replacedName || (!f.replacedName && f.name === replacedName)
          );
          return (
            <TextInput
              id={row.id}
              value={row.replacedName ?? row.name}
              size="sm"
              labelText="Field name"
              hideLabel
              onChange={(e) => onNameChange(row.id, e.target.value)}
              aria-label={`Edit field name for ${row?.fieldName}`}
              invalid={Boolean(nameValidation[row.id])}
              invalidText={nameValidation[row.id]}
            />
          );
        },
        { containsSearchWord, getComparableValue }
      ),
      sortable: true
    },
    {
      key: 'fieldName',
      header: EXTRACTED_RESULTS_FIELD_NAME,
      renderer: Object.assign((cellValue) => <span>{cellValue}</span>, { containsSearchWord, getComparableValue }),
      sortable: true
    },
    {
      key: 'value',
      header: EXTRACTED_RESULTS_VALUE,
      renderer: Object.assign((cellValue) => <span>{cellValue}</span>, { containsSearchWord, getComparableValue }),
      sortable: true
    },
    {
      key: 'confidence',
      header: EXTRACTED_RESULTS_CONFIDENCE,
      renderer: Object.assign(
        (cellValue) => (
          <ProgressBar value={parseInt(cellValue)} max={100} label={`${cellValue}%`} type="inline" size="small" />
        ),
        {
          containsSearchWord,
          getComparableValue: getPercentageComparableValue
        }
      ),
      sortable: true
    }
  ];

  return (
    <>
      <Styled.HeaderRow>
        {dataRows.length} field{dataRows.length !== 1 ? 's' : ''} extracted
      </Styled.HeaderRow>
      <EntityTable
        noMinWidth
        rows={fields}
        isSearchPersistent
        columns={tableHeaders}
        isLoading={false}
        tableSize="md"
        toolbarPlaceholder="Search input text"
        batchActions={false}
        // @ts-ignore
        emptyState={<div>No fields extracted</div>}
      />
    </>
  );
}
